import { DEVICE_TYPE } from 'config/api';

import { RBDeviceType } from 'store/railsBuilder/reducer.types';

import { MOBILE_PLAYBACK_CONTENT_SOURCE } from './utils';

export enum UavContentTypes {
    BTV = 'BTV',
    DEEPLINK = 'DEEPLINK',
    VOD = 'vod-ed',
    SO = 'start-over',
    NPVR = 'npvr-event',
    LIVE = 'live-event',
    STCU = 'live-stcu-event',
    LTCU = 'vod-ltcu',
    D2G = 'D2G',
}

export enum UavEvents {
    PLAYBACK_METRICS = 'playback_metrics',
    PLAYER_ERROR = 'playerError',
    RAIL_VIEW = 'railView',
    RAIL_SELECTION = 'railSelection',
    THIRD_PARTY_APP_START = 'thirdPartyAppStart',
    DEEPLINK_TRIGGERED = 'deepLinkTriggered',
    RETURN_TO_LAUNCHER = 'returnToLauncher',
    APP_START = 'appStart',
    APP_END = 'appEnd',
    APP_BACKGROUND = 'appBackground',
    APP_FOREGROUND = 'appForeground',
    PLAYBACK_START = 'playbackStart',
    PLAYBACK_STOP = 'playbackStop',
    PLAYBACK_PAUSE = 'playbackPause',
    PLAYBACK_RESUME = 'playbackResume',
    PLAYBACK_SKIP_AHEAD = 'playbackSkipAhead',
    PLAYBACK_SKIP_BACK = 'playbackSkipBack',
    WATCH = 'watch',
    SCHEDULE_RECORDING = 'scheduleRecording',
    PRIVACY_POLICY = 'privacyPolicy',
    AD_WATCHED = 'adWatched',
    AD_DELIVERED = 'adDelivered',
    AD_SKIPED = 'adSkipped',
    GENERIC_ERROR = 'genericError',
}

export type UavBaseParams = {
    userId: string;
    key: string;
    name: UavEvents;
    accountId: string;
    deviceId: string;
    timestamp: string;
    metadata: {
        appName: string;
        appVersion: string;
        deviceType: RBDeviceType;
        consentMask?: string;
    };
};

export type UavParams<T = UavBaseParams['metadata']> = UavBaseParams & {
    metadata: T;
};

export type PlayerErrorUavParamsMetadata = {
    contentSource?: string;
    contentType?: string;
    appSessionId?: string;
    playbackSessionId?: string;
    errorCode?: string;
    errorMsg?: string;
    playerTypeVersion?: string;
    editorialID?: string;
};

export type RailViewUavParamsMetadata = {
    contentType?: string;
    playbackSessionId?: string;
    appSessionId?: string;
    depth?: string;
    railId?: string;
    templateId?: string;
};

export type RailSelectionUavParamsMetadata = {
    contentType?: string;
    playbackSessionId?: string;
    appSessionId?: string;
    sectionId?: string;
    templateId?: string;
    railId?: string;
    editorialID?: string;
    depth?: string;
};

type PlaybackMetricsUavParamsMetadata = {
    playbackSessionId: string;
    appSessionId: string;
    playbackProgress: number;
    contentSource: typeof MOBILE_PLAYBACK_CONTENT_SOURCE;
    contentType?: string;
    selectedBitrate: number | undefined;
    availableBitrates: number[] | undefined;
    bytesDownloaded: number | undefined;
    downloadBitrate: number | undefined;
    downloadBitrateAverage: number | undefined;
    uri: string | undefined;
    bufferedDuration: number | undefined;
    streamBitrate: number | undefined;
    droppedVideoFrames: number | undefined;
    totalVideoFrames: number | undefined;
    editorialID?: string;
};

type AppStartUavParamsMetadata = {
    appSessionId: string;
    activityDateTime: string;
};

type AppEndUavParamsMetadata = {
    appSessionId: string;
    activityDateTime: string;
};

type PlaybackStartUavParamsMetadata = {
    contentSource: typeof MOBILE_PLAYBACK_CONTENT_SOURCE;
    contentType?: string;
    recordingId?: string;
    appSessionId: string;
    playbackSessionId: string;
    railId?: string;
    depth?: number;
    templateId?: string;
    editorialID: string;
    deviceType: string;
    technicalId: string;
    programmeId: string;
    seriesId?: string;
};

type PlaybackStopUavParamsMetadata = {
    contentSource: typeof MOBILE_PLAYBACK_CONTENT_SOURCE;
    contentType?: string;
    recordingId?: string;
    appSessionId: string;
    playbackSessionId: string;
    position: number;
    editorialID: string;
};

type PlaybackPauseUavParamsMetadata = {
    contentSource: typeof MOBILE_PLAYBACK_CONTENT_SOURCE;
    contentType?: string;
    recordingId?: string;
    appSessionId: string;
    playbackSessionId: string;
    position: number;
    editorialID: string;
};

type PlaybackSkipAheadUavParamsMetadata = {
    contentSource: typeof MOBILE_PLAYBACK_CONTENT_SOURCE;
    contentType?: string;
    recordingId?: string;
    appSessionId: string;
    playbackSessionId: string;
    startPosition: number;
    endPosition: number;
    editorialID: string;
};

type PlaybackSkipBackUavParamsMetadata = {
    contentSource: typeof MOBILE_PLAYBACK_CONTENT_SOURCE;
    contentType?: string;
    recordingId?: string;
    appSessionId: string;
    playbackSessionId: string;
    startPosition: number;
    endPosition: number;
    editorialID?: string;
};

type PlaybackHeartbeatUavParamsMetadata = {
    contentSource: typeof MOBILE_PLAYBACK_CONTENT_SOURCE;
    contentType?: string;
    recordingId?: string;
    appSessionId: string;
    playbackSessionId: string;
    position: number;
    editorialID: string;
};

type WatchUavParamsMetadata = {
    contentSource: typeof MOBILE_PLAYBACK_CONTENT_SOURCE;
    contentType?: string;
    recordingId?: string;
    playbackSessionId?: string;
    appSessionId: string;
    position?: number;
    railId?: string;
    templateId: string;
    contentId: string;
    editorialID?: string;
    technicalId?: string;
    programmeId?: string;
    seriesId?: string;
};

type ThirdPartyAppStartUavParamsMetadata = {
    contentSource: typeof MOBILE_PLAYBACK_CONTENT_SOURCE;
    contentType?: string;
    playbackSessionId?: string;
    appSessionId?: string;
    appName?: string;
    appReference: string;
    railId?: string;
    templateId: string;
    deeplinkProviderId: string;
    deepLinkid: string;
    providerResourceId: string;
};

type ReturnToLauncherUavParamsMetadata = {
    contentSource: typeof MOBILE_PLAYBACK_CONTENT_SOURCE;
    contentType: string;
    playbackSessionId?: string;
    appSessionId?: string;
    appName?: string;
};

type DeeplinkTriggeredUavParamsMetadata = {
    contentSource: typeof MOBILE_PLAYBACK_CONTENT_SOURCE;
    contentType?: string;
    appSessionId?: string;
    deeplinkProviderId: string;
    source?: string;
    providerResourceId: string;
    deepLinkid: string;
    railId?: string;
    templateId: string;
    editorialID?: string;
    playbackSessionId?: string;
};

type ScheduleRecordingsUavParamsMetadata = {
    contentSource: typeof MOBILE_PLAYBACK_CONTENT_SOURCE;
    contentType?: string;
    recordingId?: string;
    appSessionId?: string;
    playbackSessionId?: string;
    seriesLink?: string;
    seriesId?: string;
    editorialID?: string;
    technicalId?: string;
    programmeId?: string;
    deeplinkId?: string;
    deviceType?: typeof DEVICE_TYPE;
};

type PrivacyPolicyUavParamsMetadata = {
    policyType: 'GDPR';
    consentProvider: 'Didomi';
    consents: string;
};

export type PlayerErrorUavParams = UavParams<PlayerErrorUavParamsMetadata>;
export type RailViewUavParams = UavParams<RailViewUavParamsMetadata>;
export type RailSelectionUavParams = UavParams<RailSelectionUavParamsMetadata>;
export type PlaybackMetricsUavParams =
    UavParams<PlaybackMetricsUavParamsMetadata>;
export type AppStartUavParams = UavParams<AppStartUavParamsMetadata>;
export type AppEndUavParams = UavParams<AppEndUavParamsMetadata>;
export type PlaybackStartUavParams = UavParams<PlaybackStartUavParamsMetadata>;
export type PlaybackStopUavParams = UavParams<PlaybackStopUavParamsMetadata>;
export type PlaybackPauseUavParams = UavParams<PlaybackPauseUavParamsMetadata>;
export type PlaybackSkipAheadUavParams =
    UavParams<PlaybackSkipAheadUavParamsMetadata>;
export type PlaybackSkipBackUavParams =
    UavParams<PlaybackSkipBackUavParamsMetadata>;
export type PlaybackHeartbeatUavParams =
    UavParams<PlaybackHeartbeatUavParamsMetadata>;
export type WatchUavParams = UavParams<WatchUavParamsMetadata>;
export type ThirdPartyAppStartUavParams =
    UavParams<ThirdPartyAppStartUavParamsMetadata>;
export type ReturnToLauncherUavParams =
    UavParams<ReturnToLauncherUavParamsMetadata>;
export type DeeplinkTriggeredUavParams =
    UavParams<DeeplinkTriggeredUavParamsMetadata>;
export type ScheduleRecordingsUavParams =
    UavParams<ScheduleRecordingsUavParamsMetadata>;
export type PrivacyPolicyUavParams = UavParams<PrivacyPolicyUavParamsMetadata>;
export type PlaybackResumeUavParams = UavParams<PlaybackStartUavParamsMetadata>;

export type UavEventPayload =
    | RailViewUavParams
    | RailSelectionUavParams
    | PlayerErrorUavParams
    | PlaybackMetricsUavParams
    | ThirdPartyAppStartUavParams
    | ReturnToLauncherUavParams
    | DeeplinkTriggeredUavParams
    | AppStartUavParams
    | AppEndUavParams
    | PlaybackStartUavParams
    | PlaybackStopUavParams
    | PlaybackPauseUavParams
    | PlaybackSkipAheadUavParams
    | PlaybackSkipBackUavParams
    | PlaybackHeartbeatUavParams
    | WatchUavParams
    | PrivacyPolicyUavParams
    | PlaybackResumeUavParams;
